/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TPlayerStats } from "../../../videoplayer/types";
import type { IPlaybackSessionAccumulatorSnapshot } from "../IPlaybackSessionAccumulator";
import type { TAggregated } from "../types";

const BITS_PER_BYTE = 8;
const BYTES_PER_MEGABYTE = 1_048_576;
const BPS_PER_MBPS = 1_000_000;
const MILLISECONDS_PER_SECOND = 1_000;

/**
 * `video_start_time` analog — Shaka has no direct "initial playback start
 * delay" stat; `loadLatency` (load() -> loadeddata) is the closest signal.
 */
export const computeVideoStartTimeMs = (
  stats: TPlayerStats
): number | undefined => {
  if (!Number.isFinite(stats.loadLatency) || stats.loadLatency <= 0) {
    return undefined;
  }
  return stats.loadLatency * MILLISECONDS_PER_SECOND;
};

/**
 * Session average video bitrate (Mbps), derived from total bytes downloaded
 * over total play time — Shaka has no per-session "average video bitrate"
 * stat, so this approximates it the same way Android's fallback does.
 */
export const computeAvgVideoBitrateMbps = (
  stats: TPlayerStats
): number | undefined => {
  if (
    !Number.isFinite(stats.bytesDownloaded) ||
    !Number.isFinite(stats.playTime)
  ) {
    return undefined;
  }
  if (stats.playTime <= 0) {
    return undefined;
  }
  const bps = (stats.bytesDownloaded * BITS_PER_BYTE) / stats.playTime;
  return bps / BPS_PER_MBPS;
};

/** Session average network bitrate (Mbps), from Shaka's live bandwidth estimate. */
export const computeAvgNetworkBitrateMbps = (
  stats: TPlayerStats
): number | undefined => {
  if (
    !Number.isFinite(stats.estimatedBandwidth) ||
    stats.estimatedBandwidth <= 0
  ) {
    return undefined;
  }
  return stats.estimatedBandwidth / BPS_PER_MBPS;
};

/** Count of ABR/manual variant (not text) track switches during the session. */
export const computeProfileShiftCount = (
  stats: TPlayerStats
): number | undefined => {
  if (!Array.isArray(stats.switchHistory)) {
    return undefined;
  }
  const count = stats.switchHistory.filter(
    (entry) => entry.type === "variant"
  ).length;
  return count > 0 ? count : undefined;
};

/** Current (instantaneous) video bitrate in Mbps, from the active stream's bandwidth. */
export const computeVideoBitrateMbps = (
  stats: TPlayerStats
): number | undefined => {
  if (!Number.isFinite(stats.streamBandwidth) || stats.streamBandwidth <= 0) {
    return undefined;
  }
  return stats.streamBandwidth / BPS_PER_MBPS;
};

/**
 * Current (instantaneous) network bandwidth estimate in Mbps. Web exposes no
 * separate instantaneous estimate — same source/logic as the session average.
 */
export const computeNetworkBitrateMbps = computeAvgNetworkBitrateMbps;

/**
 * `start_offset` (spec §10) — seconds after program start when viewing
 * began. Linear content only; `undefined` for VOD or when the program
 * start time hasn't resolved.
 */
export const computeStartOffsetSeconds = (
  programStartTimeEpochSeconds: number | undefined,
  sessionStartEpochSeconds: number
): number | undefined => {
  if (
    programStartTimeEpochSeconds === undefined ||
    programStartTimeEpochSeconds <= 0
  ) {
    return undefined;
  }
  const offsetSeconds = Math.round(
    sessionStartEpochSeconds - programStartTimeEpochSeconds
  );
  return offsetSeconds >= 0 ? offsetSeconds : undefined;
};

/** `end_offset` (spec §10) — offset of the latest portion viewed. */
export const computeEndOffsetSeconds = (
  startOffsetSeconds: number | undefined,
  durationMs: number
): number | undefined => {
  if (startOffsetSeconds === undefined) {
    return undefined;
  }
  return startOffsetSeconds + Math.round(durationMs / MILLISECONDS_PER_SECOND);
};

export const buildAggregated = (
  accumulator: IPlaybackSessionAccumulatorSnapshot,
  stats: TPlayerStats,
  userErrorCount: number,
  transientCodesJoined: string | undefined,
  programStartTimeEpochSeconds?: number,
  sessionStartEpochSeconds?: number
): TAggregated => {
  const startOffsetSeconds =
    sessionStartEpochSeconds !== undefined
      ? computeStartOffsetSeconds(
          programStartTimeEpochSeconds,
          sessionStartEpochSeconds
        )
      : undefined;
  return {
    videoStartTime: computeVideoStartTimeMs(stats),
    videoPlayingStartTime:
      accumulator.firstPlayingElapsedMs !== null
        ? accumulator.firstPlayingElapsedMs
        : undefined,
    avgVideoBitrateMbps: computeAvgVideoBitrateMbps(stats),
    avgNetworkBitrateMbps: computeAvgNetworkBitrateMbps(stats),
    videoBitrateMbps: computeVideoBitrateMbps(stats),
    networkBitrateMbps: computeNetworkBitrateMbps(stats),
    megabytesReceived: Number.isFinite(stats.bytesDownloaded)
      ? stats.bytesDownloaded / BYTES_PER_MEGABYTE
      : undefined,
    videoFramesPresented: Number.isFinite(stats.decodedFrames)
      ? stats.decodedFrames
      : undefined,
    videoFramesDropped: Number.isFinite(stats.droppedFrames)
      ? stats.droppedFrames
      : undefined,
    profileShiftCount: computeProfileShiftCount(stats),
    userErrorCount: userErrorCount > 0 ? userErrorCount : undefined,
    userTransientCodes: transientCodesJoined,
    videoBufferingTime: accumulator.bufferingTimeMs,
    videoBufferingCount: accumulator.bufferingCount,
    videoStallingTime: accumulator.stallingTimeMs,
    videoStallingCount: accumulator.stallingCount,
    videoPlayingTime: accumulator.playingTimeMs,
    videoPlayingCount: accumulator.playingCount,
    videoPausingTime: accumulator.pausingTimeMs,
    videoPausingCount: accumulator.pausingCount,
    videoTrickplayingTime: accumulator.trickplayingTimeMs,
    videoTrickplayingCount: accumulator.trickplayingCount,
    startOffsetSeconds,
    endOffsetSeconds: computeEndOffsetSeconds(
      startOffsetSeconds,
      accumulator.durationMs
    )
  };
};
